import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';

import Analytics from '@web-solutions/module-analytics';
import { isFoodzeeBackend } from '@web-solutions/react-billing';
import { useRemoteConfig } from 'core/hooks/use-remote-config';

import { useStore } from '../store/store';
import { getEnvUrl } from '../helpers/getEnvUrl';
import { applyFoodzeeAuthTokens } from '../helpers/applyFoodzeeAuthTokens';

/**
 * Deferred-email flow: when `collectEmailAfterPayment` is enabled and the payment
 * system runs on the Foodzee backend (Stripe or TailoredPay), create an anonymous
 * user (email = null) when the user reaches the personal-plan (paywall) page.
 * This lets the user reach checkout and pay without entering an email — which
 * lifts conversion. The email is collected after payment on the dedicated
 * `email-after-payment` page via `/user/update-by-token`.
 */
export function useAnonymousUser(isRemoteConfigLoaded: boolean = true) {
  const dispatch = useDispatch();
  // @ts-ignore — collectEmailAfterPayment lives on the core RemoteConfig type
  const { paymentSystem, collectEmailAfterPayment } = useRemoteConfig();
  // Create the email=null user up front ONLY when deferring email collection to
  // after payment. With `collectEmailAfterPayment` off, the email popup collects
  // the email and creates the user before checkout, so no anonymous user here;
  // hosted Checkout still has a `startStripeCheckout` fallback that creates one
  // on the pay click if the popup was skipped.
  const needsAnonymousUser = collectEmailAfterPayment;

  const userId = useStore((state) => state.userId);
  const authTokens = useStore((state) => state.authTokens);
  const setUserId = useStore((state) => state.setUserId);

  const requestedRef = useRef(false);
  const { apiBaseUrl } = getEnvUrl();

  useEffect(() => {
    if (!isRemoteConfigLoaded) return;
    if (!isFoodzeeBackend(paymentSystem) || !needsAnonymousUser) return;
    if (requestedRef.current) return;

    // Returning visitor with usable credentials — re-apply the persisted token so
    // billing is initialized, and don't create a second anonymous user.
    //
    // NOTE: the check is on the token, NOT on `userId`. Both are persisted, but the
    // token is dropped once it expires (see the store's `merge`), and a bare
    // `userId` authenticates nothing: bailing out on it would leave the session with
    // no billing auth at all, which fails every /nmi request and drops the funnel
    // onto the reserve payment system. So a stored id without a live token falls
    // through and a fresh anonymous user is created below.
    if (authTokens?.accessToken) {
      requestedRef.current = true;
      applyFoodzeeAuthTokens(
        {
          accessToken: authTokens.accessToken,
          refreshToken: authTokens.refreshToken,
          uid: authTokens.userId || userId,
        },
        dispatch,
        { reinitBilling: true, paymentSystem }
      );
      return;
    }

    requestedRef.current = true;

    (async () => {
      try {
        const response = await axios.post(`${apiBaseUrl}/v2/user`);

        if (response.status === 201) {
          setUserId(response.data.uid);
          applyFoodzeeAuthTokens(response.data, dispatch, {
            reinitBilling: true,
            paymentSystem,
          });
          Analytics.trackEvent('anonymous_user', 'created');
        }
      } catch (error) {
        // Allow a retry on the next render if the request failed.
        requestedRef.current = false;
        Analytics.trackEvent('anonymous_user', 'error');
      }
    })();
  }, [
    apiBaseUrl,
    authTokens,
    needsAnonymousUser,
    dispatch,
    isRemoteConfigLoaded,
    paymentSystem,
    setUserId,
    userId,
  ]);
}
